import { TBaseItem } from "@centricsoftware/design-system";
import { C8Node, C8NodeType, cnl, IDataStoreNamesItem, NODE_TYPE } from "c8-platform";
import { getTypeAttributeDestination } from "csi/reflection/reflection";
import siteDefaultLocale from "csi/siteDefaultLocale";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";

// This list was previously determined dynamically by traversing all subtyped classes and finding those that
// have a properly named enum and are also a BO. This long process results in only 2 results, and the 2 other
// ProductType results were already hard-coded. Rather than inadvertently introducing another one, force
// developer to add explicitly (or push logic to server).
const SUBTYPE_ENUMS = {
	ContractualDocument: {
		enum: "ContractualDocumentSubtype",
		path: "(ContractualDocument)",
		url: "centric://REFLECTION/BuildInAttribute/ContractualDocument/Subtype",
	},
	ContractualDocumentRevision: {
		enum: "ContractualDocumentSubtype",
		path: "(ContractualDocumentRevision)Child:__Parent__(ContractualDocument)",
		url: "centric://REFLECTION/BuildInAttribute/ContractualDocument/Subtype",
	},
	Material: {
		enum: "MaterialSubtype",
		path: "(Material)",
		url: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
	},
	MaterialAttributes: {
		enum: "MaterialSubtype",
		path: "(MaterialAttributes)Child:__Parent__(Material)",
		url: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
	},
	Style: {
		enum: "StyleSubtype",
		path: "(Style)",
		url: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
	},
	StyleAttributes: {
		enum: "StyleSubtype",
		path: "(StyleAttributes)Child:__Parent__(Style)",
		url: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
	},
	TestSpec: {
		enum: "TestSpecSubtype",
		path: "(TestSpec)",
		url: "centric://REFLECTION/BuildInAttribute/TestSpec/Subtype",
	},
	TestSpecRevision: {
		enum: "TestSpecSubtype",
		path: "(TestSpecRevision)Child:__Parent__(TestSpec)",
		url: "centric://REFLECTION/BuildInAttribute/TestSpec/Subtype",
	},
};

/** return select option for configurable attribute (always in site locale) */
export function createConfigurableAttributeSelectOption(node: C8Node, loc?: ILocaleProvider): TBaseItem {
	return {
		id: node.$URL,
		name: siteDefaultLocale.getConfigurableAttributeNodeName(node),
	};
}

/** return target class and subtype for given attribute */
export function getAttributeTargetClassAndSubtype(attributeNode: C8Node): [string, string] {
	const { 
		ReflectionAttributeId,
		ReflectionAttributeOwner,
		Subtype,
		TargetClassName,
	} = attributeNode;
	let targetClass: C8NodeType;
	let subtypeUrl: cnl;

	if (isCustomAttribute(attributeNode)) {
		targetClass = TargetClassName;
		subtypeUrl = Subtype;
	} else {
		targetClass = getTypeAttributeDestination(ReflectionAttributeOwner, ReflectionAttributeId)?.type;
	}

	return [targetClass || "", subtypeUrl || ""];
}

/** return option for subtype attribute of the given class if enumList matches */
export function getDependsOnSubtypeEnumUrl(sourceClass: C8NodeType, enumList: string): cnl | undefined {
	const item = SUBTYPE_ENUMS[sourceClass];

	if (item && (item.enum === enumList)) {
		return item.url;
	}
}

/** return path for subtype enum of given class */
export function getDependsOnSubtypeEnumPath(sourceClass: C8NodeType): string | undefined {
	return SUBTYPE_ENUMS[sourceClass]?.path;
}

/** return true if given attribute is custom; if no attribute provided, then assume true since new attributes must be custom */
export function isCustomAttribute(attribute: C8Node | undefined): boolean {
	return !attribute || (attribute.$Type === NODE_TYPE.CUSTOM_ATTRIBUTE);
}

